/**
 * 语文听写的**维护类**接口（系统管理员专属）—— `/admin/dictation/*`。
 *
 * ## 为什么要单独一个文件（而不是留在 `dictation.ts` 里）
 *
 * 2026-10-16 起字表是**站点资产**：维护归系统管理员，使用归家长。两件事的
 * 路径、门禁、能不能被看见**完全不同**，所以前端也分成两个模块：
 *
 * | 文件 | 装什么 | 谁能用 |
 * | --- | --- | --- |
 * | `dictation.ts` | 读（浏览/查询）+ 取词 | 家长（+ 管理员复用同一份读） |
 * | **本文件** | 新建/修改/删除/导入/**拼音人工覆盖** | **只有管理员** |
 *
 * ⛔ 家长端页面**不要 import 这个文件**。这不是"把按钮藏起来"：
 *   后端那 21 条路径**对家长根本不存在**（它们挂在 `/admin/dictation/*`，
 *   过 `requireSystemAdmin` 门禁）。前端跟着分文件，是让"家长不可能写"这件事
 *   在**代码里**就能一眼看出来（而不是靠 review 时逐行找按钮）。
 *
 * ## 端点清单（21 条维护 + 1 条"查现有关联"的辅助读）
 *
 * | 方法 | 路径 | 说明 |
 * | --- | --- | --- |
 * | POST/PATCH | `/admin/dictation/textbooks[/:textbookId]` | 课本新建 / 改 |
 * | POST/PATCH/DELETE | `/admin/dictation/lessons[/:lessonId]` | 课文增改删 |
 * | POST/PATCH/DELETE | `/admin/dictation/hanzi[/:hanziId]` | 汉字增改删 |
 * | PUT | `/admin/dictation/hanzi/:hanziId/pinyin` | **拼音人工覆盖**（`""` = 恢复自动） |
 * | POST/PATCH/DELETE | `/admin/dictation/words[/:wordId]` | 词语增改删 |
 * | PUT | `/admin/dictation/words/:wordId/pinyin` | 整词拼音（按词定音） |
 * | PUT | `/admin/dictation/words/:wordId/readings` | 逐位读音（多音字按音造词） |
 * | POST/PUT/PATCH/DELETE | `/admin/dictation/hanzi/:hanziId/listings[/:listingId]` | 收录关系 |
 * | POST | `/admin/dictation/lists/import` | 按「书 + 表」整块粘贴 |
 * | POST | `/admin/dictation/import/preview` \| `/import/commit` | 通用导入两步 |
 *
 * ⚠️ **拼音的人工覆盖归这里**（不是家长端）：它是**字表数据**的一部分 ——
 *   读错音会教坏所有用户的孩子，而正确答案只有一个。与"给这个词录一段我自己的音"
 *   （家庭专属，在家长端）是两件事。
 *
 * ## 解码**不重写**
 *
 * 所有 `decode*` 都从 `./dictation` import（那一份是逐字对照生产响应确认过的）。
 * 这里只负责"打到哪条路径上"。两份解码 = 两份口径 = 迟早不一致。
 */
import {
  decodeHanzi,
  decodeHanziListing,
  decodeImportReport,
  decodeLesson,
  decodeTextbook,
  decodeWord,
  dictationBase,
  listWords,
} from './dictation';
import type {
  HanziListingView,
  HanziView,
  ImportReportView,
  LessonView,
  TextbookView,
  WordView,
} from './dictation';
import { api } from './client';

/** 管理端路径前缀。与家长端的 `/students/:id/dictation` **不是**同一个门禁。 */
const ADMIN_SCOPE = { kind: 'admin' } as const;

// ---------------------------------------------------------------- 载荷类型

export interface CreateTextbookPayload {
  publisher: string;
  name: string;
  /** 年级 / 学期：**一本书 = 一册**，两个都必填。 */
  gradeCode: string;
  semester: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface CreateLessonPayload {
  textbookId: string;
  gradeCode: string;
  semester: string;
  title: string;
  lessonLabel?: string;
  lessonNo?: number;
  unitNo?: number | null;
  sortOrder?: number;
}

/** `PATCH .../lessons/:id` 的载荷（`updateLessonSchema`：不含 `textbookId`）。 */
export interface UpdateLessonPayload {
  gradeCode?: string;
  semester?: string;
  title?: string;
  lessonLabel?: string;
  lessonNo?: number;
  unitNo?: number | null;
  sortOrder?: number;
}

export interface CreateHanziPayload {
  textbookId: string;
  charText: string;
  pinyin?: string;
  gradeCode?: string | null;
  semester?: string | null;
  /**
   * 手工加这个字时顺手收录到哪张表（`shizi` / `xiezi`）。
   * `null` = **只建字、不收录**；省略 = 后端默认写字表。
   */
  listType?: string | null;
  /** 收录的年级/学期（省略时与 `gradeCode` / `semester` 同册）。 */
  listGradeCode?: string;
  listSemester?: string;
  note?: string;
  lessonIds?: string[];
}

/** `PATCH .../hanzi/:id` 的载荷（`updateHanziSchema`：身份字段不可改）。 */
export interface UpdateHanziPayload {
  pinyin?: string | null;
  gradeCode?: string | null;
  semester?: string | null;
  note?: string;
  lessonIds?: string[];
}

export interface CreateWordPayload {
  textbookId: string;
  wordText: string;
  pinyin?: string;
  category?: string;
  fromTextbook?: boolean;
  gradeCode?: string | null;
  semester?: string | null;
  note?: string;
  lessonIds?: string[];
  /** 每个字在这个词里读哪个音（按位置）。省略 = 后端从整词拼音推。 */
  charReadings?: string[];
}

/** `PATCH .../words/:id` 的载荷（`updateWordSchema`）。 */
export interface UpdateWordPayload {
  /**
   * **改字**（2026-10-30）：只有**自己造的词**能改；课本词表由服务端拒（400）。
   * 改了字，拼音会按新字重算（除非同时给了 `pinyin`）。
   */
  wordText?: string;
  pinyin?: string | null;
  category?: string;
  fromTextbook?: boolean;
  gradeCode?: string | null;
  semester?: string | null;
  note?: string;
  lessonIds?: string[];
  charReadings?: string[];
}

/**
 * **按「书 + 表」整块粘贴**：预览（`dryRun: true`，一行都不写）或提交。
 *
 * 与 `previewImport` / `commitImport` 的关系：那两个是"一份文本里什么都能有"的
 * 通用入口，这个是"一本书的一张表"的专用入口 —— 课本/年级/学期/表类型不用写在
 * 每一行里。解析器是**同一个**，所以"逐行报错 + 有效行照常导入"完全一致。
 */
export interface ListImportPayload {
  /**
   * 要录进哪一册：**两种给法，二选一**（后端 `listImportSchema` 同一套口径）。
   *
   *   · `textbookId` —— 三格定位到了已有那一册；
   *   · `publisher`  —— 三格还没有对应的书（提交时后端**自动建**；预览一行都不写）。
   */
  textbookId?: string;
  /** 出版社**人话**（`人民教育出版社`）。只有"书还没建过"时才发。 */
  publisher?: string;
  gradeCode: string;
  semester: string;
  listType: string;
  text: string;
  /**
   * 词条类别（`common` / `phrase` / `idiom`）。
   *
   * ⚠️ 2026-10-23 起**「按书录入」不再发这个键**（用户要求删掉那个下拉）：
   *    不传时后端按默认 `phrase`（词语）落库。字段本身与后端 schema 都保留 ——
   *    别的调用方（如通用导入）仍可显式指定。
   */
  wordCategory?: string;
  /**
   * **预览里给重复出现的字选的读音**（迁移 0018）：
   * `{line, index, reading}`，位置 = 第几行第几个字。
   * 与**同一份 `text`** 一起发 —— 预览与提交仍然是同一份输入。
   */
  readings?: { line: number; index: number; reading: string }[];
  dryRun: boolean;
  maxRows?: number;
  maxIssues?: number;
}

/**
 * 通用导入请求体。
 *
 * ⚠️ **预览与提交必须发同一份 `text`**（页面把预览时那一份原样存下来再提交）：
 *   `preview` 与 `commit` 在后端**共用同一条代码路径**，只有最后写不写库不同 ——
 *   所以"预览说新增 8 条"就是"提交会新增 8 条"，前提是**输入一字未改**。
 */
export interface ImportPayload {
  text: string;
  textbook?: string;
  /**
   * 默认年级 / 学期（文本里没写时用它）。
   *
   * ⚠️ 课本按册建（一本书 = 一册）：`textbook` 给的是名字时，必须能从这里
   *    （或文本的「年级」「学期」列）确定是哪一册；给 id 时用书的册别。
   * ⛔ 版次已经彻底不用（2026-10-18）：文本里即使还有「版次」列，也会被忽略。
   */
  gradeCode?: string;
  semester?: string;
  /** 默认表类型（识字表 / 写字表 / 词语表）—— 文本里没写表名时用它。 */
  listType?: string;
  /** 词条的默认类别（整段就是词语表粘贴时用）。 */
  wordCategory?: string;
  maxIssues?: number;
}
export interface ImportCommitPayload extends ImportPayload {
  maxRows?: number;
}

// ---------------------------------------------------------------- 端点：课本

export async function createTextbook(payload: CreateTextbookPayload): Promise<TextbookView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/textbooks`, payload);
  return decodeTextbook(data);
}

export async function updateTextbook(
  textbookId: string,
  payload: Partial<CreateTextbookPayload>,
): Promise<TextbookView> {
  const data = await api.patch<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/textbooks/${textbookId}`,
    payload,
  );
  return decodeTextbook(data);
}

// ---------------------------------------------------------------- 端点：课文

export async function createLesson(payload: CreateLessonPayload): Promise<LessonView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/lessons`, payload);
  return decodeLesson(data);
}

export async function updateLesson(
  lessonId: string,
  payload: UpdateLessonPayload,
): Promise<LessonView> {
  const data = await api.patch<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/lessons/${lessonId}`,
    payload,
  );
  return decodeLesson(data);
}

/** 删课文（**字与词本身保留**，只清掉关联）。`deleted: false` = 本来就不在了。 */
export async function deleteLesson(lessonId: string): Promise<boolean> {
  const data = await api.del<unknown>(`${dictationBase(ADMIN_SCOPE)}/lessons/${lessonId}`);
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：选词造词（候选词库）

/** 一条候选词（来自 `dictation_word_bank`，迁移 0022）。 */
export interface WordCandidate {
  wordText: string;
  category: string;
  /** 现算的拼音（词库本身没有拼音）。 */
  pinyin: string;
  /** 注意：**已存在的词服务端已经过滤掉了**，不会出现在候选里。 */
}

/**
 * **「选词造词」候选词**（2026-11-04 用户要求）：某个字在权威词库里能组的词。
 *
 * 只读查询，不写任何东西 —— 用户勾选之后才走 `createWord()` 落库。
 *
 * **分批**：按词频从高到低排，一次只拿一页；「显示下一批」把这个响应里的
 * `nextOffset` 再传回来（`null` = 没有了）。
 */
export async function listWordCandidates(
  hanziId: string,
  options: { limit?: number; offset?: number; reading?: string } = {},
): Promise<{
  charText: string;
  total: number;
  nextOffset: number | null;
  candidates: WordCandidate[];
}> {
  const limit = options.limit ?? 60;
  const offset = options.offset ?? 0;
  /**
   * 多音字按音选词：只拿"这个字在这个词里读这个音"的词
   * （2026-11-04 用户报的："这是'校对'的'校'，你选的却是'学校'的'校'"）。
   */
  const readingQuery =
    options.reading !== undefined && options.reading !== ''
      ? `&reading=${encodeURIComponent(options.reading)}`
      : '';
  const data = await api.get<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/word-candidates?limit=${limit}&offset=${offset}${readingQuery}`,
  );
  const record = (data ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(record.candidates) ? record.candidates : [];
  const nextRaw = record.nextOffset;
  return {
    charText: typeof record.charText === 'string' ? record.charText : '',
    total: typeof record.total === 'number' ? record.total : 0,
    nextOffset: typeof nextRaw === 'number' ? nextRaw : null,
    candidates: raw.filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null).map((item) => ({
      wordText: typeof item.wordText === 'string' ? item.wordText : '',
      category: typeof item.category === 'string' ? item.category : 'phrase',
      pinyin: typeof item.pinyin === 'string' ? item.pinyin : '',
    })),
  };
}

// ---------------------------------------------------------------- 端点：汉字

export async function createHanzi(payload: CreateHanziPayload): Promise<HanziView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/hanzi`, payload);
  return decodeHanzi(data);
}

export async function updateHanzi(
  hanziId: string,
  payload: UpdateHanziPayload,
): Promise<HanziView> {
  const data = await api.patch<unknown>(`${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}`, payload);
  return decodeHanzi(data);
}

/**
 * 只改单字拼音覆盖（**全站共享的字表数据**）。
 *
 * ⚠️ `''` / `null` = **恢复自动值**（不是"读成空"）—— 界面上"恢复自动"就发空串。
 *
 * ⚠️ 2026-10-21（用户报的 bug「改了拼音，外面的卡片没变」）：字卡显示的是
 *    `listingReading ?? pinyin`（**位置标定的音优先**），而导入时每个位置的读音
 *    会被快照进 `hanzi_listing.reading`。所以"从某张字卡点进来改的"必须把
 *    **那个位置**也一起带上（`listingId`）—— 服务端会把它那一行显式 `reading`
 *    一起改掉；别的显式位置保持不动（语义见 `dictation.service.ts` 的
 *    `setHanziPinyin()`）。
 *
 * ⚠️ 不传 `listingId` ⇒ 请求体与改造前**逐字一致**（只改字的默认音）。
 */
export async function setHanziPinyin(
  hanziId: string,
  pinyin: string | null,
  listingId?: string | null,
): Promise<HanziView> {
  const body: { pinyin: string | null; listingId?: string } = { pinyin };
  /**
   * ⚠️ 空串 / null 都**不发**这个字段：老后端收到的请求体与以前完全一样
   *    （不会因为多一个 `null` 被 zod 的旧 schema 拒掉）。
   */
  if (listingId !== undefined && listingId !== null && listingId !== '') {
    body.listingId = listingId;
  }
  const data = await api.put<unknown>(`${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/pinyin`, body);
  return decodeHanzi(data);
}

/**
 * **只改「教学口径的多音字」标注**（标为多音字 / 标为单音字）。
 *
 * ⚠️ 这是**人工标注**，不是自动判断：默认 `false` = 单音字。
 *    "这个字在拼音表里有几个读音"是技术层的另一件事（`readings` / `pinyinAlternates`），
 *    仍然用于读音选择与按音造词 —— 那个口径会把变调/古音/生僻音都算进来，
 *    280 个一年级生字会被自动标出 134 个多音字。
 */
export async function setHanziPolyphonic(
  hanziId: string,
  polyphonic: boolean,
): Promise<HanziView> {
  const data = await api.put<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/polyphonic`,
    { polyphonic },
  );
  return decodeHanzi(data);
}

/**
 * **只改「这个字隐藏了哪几个读音」**（`PUT .../hanzi/:id/hidden-readings`，迁移 0016）。
 *
 * ## 为什么它在这里、而且只有这一个入口
 *
 * 用户原话："**第一个，隐藏读音要存服务器。**" —— 隐藏从此是**站点字表数据**：
 * 管理员藏了什么，家里所有设备（包括家长端，只读）看到的都是藏起来的样子。
 *
 * ⚠️ 上一版是 `localStorage`（`lib/hanziReadingPrefs.ts` 的旧实现），**已删除**：
 *    两套并存会让"到底哪一份算数"永远说不清（见那个文件的新注释）。
 * ⚠️ 传**整组**（空数组 = 全部显示）：这一列的写语义就是"整体替换"，
 *    没有"加一个 / 删一个"的端点 —— 也就没有"并发点两下丢一个"的中间态。
 * ⚠️ 家长端**不 import 这个模块**（`dictationAnchor.test.ts` 守着这一条）。
 */
export async function setHanziHiddenReadings(
  hanziId: string,
  hidden: readonly string[],
): Promise<HanziView> {
  const data = await api.put<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/hidden-readings`,
    { hidden },
  );
  return decodeHanzi(data);
}

export async function deleteHanzi(hanziId: string): Promise<boolean> {
  const data = await api.del<unknown>(`${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}`);
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：词语

export async function createWord(payload: CreateWordPayload): Promise<WordView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/words`, payload);
  return decodeWord(data);
}

export async function updateWord(wordId: string, payload: UpdateWordPayload): Promise<WordView> {
  const data = await api.patch<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/words/${wordId}`,
    payload,
  );
  return decodeWord(data);
}

/** 只改整词拼音（**按词定音的唯一入口**）。`''` / `null` = 恢复自动值。 */
export async function setWordPinyin(wordId: string, pinyin: string | null): Promise<WordView> {
  const data = await api.put<unknown>(`${dictationBase(ADMIN_SCOPE)}/words/${wordId}/pinyin`, {
    pinyin,
  });
  return decodeWord(data);
}

/**
 * 只改**逐位读音**（"这个字在这个词里读哪个音"）—— 多音字按音造词。
 *
 * `readings` 的长度必须与词长一致；空串 = 清除那个位置的读音
 * （回到"从整词拼音推"）。
 */
export async function setWordCharReadings(
  wordId: string,
  readings: string[],
): Promise<WordView> {
  const data = await api.put<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/words/${wordId}/readings`,
    { readings },
  );
  return decodeWord(data);
}

export async function deleteWord(wordId: string): Promise<boolean> {
  const data = await api.del<unknown>(`${dictationBase(ADMIN_SCOPE)}/words/${wordId}`);
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：收录（识字表 / 写字表）

/** 新增一条收录（"这个字也在那张表上"）。幂等：已存在时返回已有的那一条。 */
export async function createListing(
  hanziId: string,
  payload: {
    textbookId: string;
    gradeCode: string;
    semester: string;
    listType: string;
    sortOrder?: number;
  },
): Promise<HanziListingView> {
  const data = await api.post<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/listings`,
    payload,
  );
  return decodeHanziListing(data);
}

/** 删一条收录。`deleted: false` = 本来就不在。 */
export async function deleteListing(hanziId: string, listingId: string): Promise<boolean> {
  const data = await api.del<unknown>(
    `${dictationBase(ADMIN_SCOPE)}/hanzi/${hanziId}/listings/${listingId}`,
  );
  return isRecord(data) && data['deleted'] === true;
}

// ---------------------------------------------------------------- 端点：导入

/** **按「书 + 表」整块粘贴**：预览（`dryRun: true`，一行都不写）或提交。 */
export async function importList(payload: ListImportPayload): Promise<ImportReportView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/lists/import`, payload);
  return decodeImportReport(data);
}

/** 导入预览（**一行都不写**）。 */
export async function previewImport(payload: ImportPayload): Promise<ImportReportView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/import/preview`, payload);
  return decodeImportReport(data);
}

/** 导入提交（与预览同一份 `text`；有效行照常导入，坏行只报告）。 */
export async function commitImport(payload: ImportCommitPayload): Promise<ImportReportView> {
  const data = await api.post<unknown>(`${dictationBase(ADMIN_SCOPE)}/import/commit`, payload);
  return decodeImportReport(data);
}

// ---------------------------------------------------------------- 辅助读（维护界面专用）

/**
 * 一个生字/词语**当前关联了哪些课文**。
 *
 * 为什么要单独一个函数：`PATCH .../hanzi/:id` 的 `lessonIds` 语义是
 * **整组替换**（给空数组 = 清空全部关联）。而**列表响应里根本没有这个字段**
 * （见 `HanziView.lessonIds` 的说明），所以"编辑时把表单默认成 `[]` 再提交"
 * 会把管理员的关联**静默清空**。要改关联，必须先把这个查出来。
 *
 * 返回：
 *   · `{ ids, total }` —— 查到了（`total` 是后端那一份列表的总数，用于判断是否被截断）；
 *   · `null` —— **没查到**（响应形状不符 / 接口出错），界面要说出来而不是当成"没有关联"。
 *
 * ⚠️ 词语这一支只能"从列表里按 id 找到自己那一行"，所以受 `limit`（≤500）限制 ——
 *   一个课本最多也就几百个词，真超过时把 `truncated` 带出去让界面提示，
 *   而不是**悄悄拿一份不全的关联去替换**（那等于删掉一部分关联）。
 */
export interface LessonLinksResult {
  ids: string[];
  /** 后端那一份列表的总数；`null` = 没拿到。 */
  total: number | null;
  /** true = 列表被 `limit` 截断，这份 `ids` 可能不全。 */
  truncated: boolean;
}

export async function fetchLessonLinks(
  kind: 'hanzi' | 'word',
  id: string,
): Promise<LessonLinksResult | null> {
  if (kind === 'hanzi') {
    const data = await api.get<unknown>(`${dictationBase(ADMIN_SCOPE)}/hanzi/${id}/words`);
    const record = isRecord(data) ? data : {};
    const hanzi = isRecord(record['hanzi']) ? record['hanzi'] : {};
    const raw = hanzi['lessonIds'];
    const ids = Array.isArray(raw)
      ? raw.filter((item): item is string => typeof item === 'string')
      : null;
    return ids === null ? null : { ids, total: null, truncated: false };
  }
  const limit = 500;
  /**
   * ⚠️ 这里复用**读**接口（`dictation.ts` 的 `listWords`），走管理员的读路径。
   *   它是只读的，与维护门禁无关。
   */
  const { words, total } = await listWords(ADMIN_SCOPE, { limit });
  const found = words?.find((word) => word.id === id);
  if (found === undefined || found.lessonIds === null) return null;
  return {
    ids: found.lessonIds,
    total,
    truncated: total !== null && words !== null && total > words.length,
  };
}

/** 与 `dictation.ts` 同一份"是不是对象"的判断（这一层只用来读一两个字段）。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
